import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import useAsyncData from '../hooks/useAsyncData';
import useStaleWarning from '../hooks/useStaleWarning';
import useAutoRefresh from '../hooks/useAutoRefresh';

const GATEWAY_BASE = '/api';

const TokenMonitor = () => {
  const { data: tokens, loading, error, retry } = useAsyncData(`${GATEWAY_BASE}/tokens`);
  const { isStale, lastUpdated } = useStaleWarning(tokens);
  useAutoRefresh(retry);

  const fmtTime = (d) => d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '–';

  if (loading) {
    return (
      <ErrorBoundary>
        <article className="panel token-panel" aria-busy="true">
          <h2>Token Monitor</h2>
          <div className="skeleton">
            <div className="skeleton-line" />
            <div className="skeleton-line" />
          </div>
        </article>
      </ErrorBoundary>
    );
  }

  if (error) {
    return (
      <ErrorBoundary>
        <article className="panel token-panel" aria-live="assertive">
          <h2>Token Monitor</h2>
          <p className="error-msg">Failed to load token usage.</p>
          <button type="button" className="btn-retry" onClick={retry}>Retry</button>
        </article>
      </ErrorBoundary>
    );
  }

  if (!tokens) {
    return (
      <ErrorBoundary>
        <article className="panel token-panel">
          <h2>Token Monitor</h2>
          <p>No token data available.</p>
        </article>
      </ErrorBoundary>
    );
  }

  const sessions = tokens.sessions || [];
  const limit = tokens.contextLimit || 272000;
  const total = sessions.reduce((sum, s) => sum + (s.tokens || 0), 0);
  const sorted = [...sessions].sort((a, b) => b.tokens - a.tokens);

  return (
    <ErrorBoundary>
      <article className="panel token-panel" aria-label="Token usage" aria-busy="false">
        <div className="panel-header">
          <h2>Token Monitor</h2>
          <div className="panel-meta">
            <span className="last-updated" aria-label="Last updated">
              Last updated: {fmtTime(lastUpdated)}
            </span>
            {isStale && (
              <span className="badge badge-stale" role="status">Stale</span>
            )}
            <button type="button" className="btn-icon btn-refresh" onClick={retry} aria-label="Refresh tokens">
              ↻
            </button>
          </div>
        </div>
        <div className="token-summary" aria-label="Total token summary">
          <strong>Total: {total.toLocaleString()} tokens</strong>
          <span className="token-limit">Context limit: {limit.toLocaleString()}/session</span>
        </div>
        <ul className="token-sessions" aria-label="Per-session token usage">
          {sorted.length === 0 && <li className="empty">No sessions</li>}
          {sorted.map((s) => {
            const pct = limit > 0 ? ((s.tokens / limit) * 100).toFixed(1) : '0';
            return (
              <li key={s.key} className="token-session-row">
                <div className="token-session-info">
                  <strong>{s.key}</strong>
                  <span className="token-session-model">{s.model}</span>
                </div>
                <div className="token-session-usage">
                  <span className="token-count">{s.tokens.toLocaleString()}</span>
                  <span className="token-pct">({pct}%)</span>
                </div>
              </li>
            );
          })}
        </ul>
      </article>
    </ErrorBoundary>
  );
};

export default TokenMonitor;
